import { env } from "@/lib/env";
import { toast } from "react-toastify";
import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import {
  ResponseChat,
  Message,
  ChatsState,
  EMPTY_STATE,
  StoredChat,
} from "./types";
import { apiFetch } from "@/lib/apiFetch";

type SocketMessage =
  | {
      action: "createChat";
      status: number;
      chat: ResponseChat;
    }
  | {
      action: "deleteMessage";
      status: number;
      conversation_id: string;
      message: {
        conversation_id: string;
        message_id: string;
        is_deleted: boolean;
      };
    }
  | {
      action: "getChatMessages";
      status: number;
      data: {
        conversation_id: string;
        messages: Message[];
        last_cursor: Record<string, any> | null;
        has_more_pages: boolean;
      };
    }
  | {
      action: "getChats";
      status: number;
      data: {
        chats: ResponseChat[];
        last_cursor: Record<string, any> | null;
        has_more_pages: boolean;
        total_unread_count: number;
      };
    }
  | {
      action: "getChatById";
      status: number;
      data: {
        chat: ResponseChat | null;
      };
    }
  | {
      action: "markAsRead";
      status: number;
      conversation_id: string;
    }
  | {
      action: "sendMessage";
      status: number;
      conversation_id: string;
      message: Message;
    }
  | {
      action: "typing";
      conversation_id: string;
      is_typing: boolean;
      sender_id: string;
    }
  | {
      action: "chatPresence";
      conversation_id: string;
      user_sub: string;
      is_active: boolean;
    }
  | {
      action: "error";
      status: number;
      code: string;
      message: string;
    };

type SocketStore = {
  socket: WebSocket | null;
  chatsState: ChatsState;
  isConnected: boolean;
  isLoading: boolean;
  openChatId: string | null;
  searchQuery: string;
  isSearching: boolean;
  isSearchMode: boolean;
  idToken: string | null;
  heartbeatTimer: NodeJS.Timeout | null;
  tokenRefreshFn: (() => Promise<string | null>) | null;
  searchChatsFn: (query: string) => Promise<void>;
  clearSearch: () => void;
  setOpenChatId: (chatId: string) => void;
  setActiveChat: (
    conversationId: string,
    secondUserSub: string,
    isActive: boolean,
  ) => void;
  connect: (idToken: string, refreshFn?: () => Promise<string | null>) => void;
  dispatchMessage: (data: SocketMessage) => void;
  disconnect: () => void;
  createChat: (
    messageType: string,
    payload: Record<string, any>,
    secondUserSub: string,
    isAiChat?: boolean,
  ) => string | null;
  deleteMessage: (
    conversationId: string,
    messageId: string,
    secondUserSub: string,
  ) => void;
  getChatMessages: (
    conversationId: string,
    cursor: Record<string, any> | null,
  ) => void;
  getChats: (cursor: Record<string, any> | null, locale: string) => void;
  getChatById: (conversationId: string) => void;
  markAsRead: (conversationId: string) => void;
  sendMessage: (
    conversationId: string,
    messageType: string,
    payload: Record<string, any>,
    secondUserSub: string,
  ) => void;
  updateMessages: (
    conversationId: string,
    fn: (prev: Message[]) => Message[],
  ) => void;
};

const CHATWEBSOCKETENDPOINT = env.NEXT_CHATS_SOCKET_API_URL as string;

export const useChatStore = create<SocketStore>((set, get) => ({
  socket: null,
  chatsState: EMPTY_STATE,
  isConnected: false,
  isLoading: true,
  openChatId: null,
  searchQuery: "",
  isSearching: false,
  isSearchMode: false,
  idToken: null,
  heartbeatTimer: null,
  tokenRefreshFn: null,
  searchChatsFn: async (query) => {
    set((state) => ({
      isSearchMode: true,
      isSearching: true,
      searchQuery: query,
      openChatId: null,
      chatsState: {
        ...state.chatsState,
        chats: [],
        last_cursor: null,
        has_more_pages: false,
      },
    }));

    try {
      set({ isLoading: true });
      const res = await apiFetch(
        `/api/chats/searchUserConversations?query=${encodeURIComponent(query)}`,
        {
          method: "GET",
        },
      );

      const data = await res.json();
      console.log(data);
      if (!res.ok) {
        throw data;
      }
      const chats: StoredChat[] = data.chats.map((chat: ResponseChat) => ({
        ...chat,
        messages: [],
        last_cursor: null,
        has_more_pages: true,
      }));

      set((state) => ({
        chatsState: {
          ...state.chatsState,
          chats,
          last_cursor: null,
          has_more_pages: false,
        },
      }));
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : err?.message;
      if (
        (err instanceof Error && err.message === "UNAUTHORIZED") ||
        errorMessage === "Internal Server Error"
      ) {
        return;
      }
      const message = err?.message || "Search failed. Please try again.";

      toast(message, { type: "error" });
    } finally {
      set({
        isSearching: false,
        isLoading: false,
      });
    }
  },

  clearSearch: () => {
    if (!get().isSearchMode) return;
    set({
      isSearchMode: false,
      isSearching: false,
      searchQuery: "",
      isLoading: true,
      openChatId: null,
    });
    // refetch normal chats
    get().getChats(null, "en");
  },
  setOpenChatId: (chatId) => {
    const prevOpenId = get().openChatId;
    const chats = get().chatsState.chats;

    if (prevOpenId && prevOpenId !== chatId) {
      const oldChat = chats.find((c) => c.conversation_id === prevOpenId);
      if (oldChat)
        get().setActiveChat(
          oldChat.conversation_id,
          oldChat.receiver_id,
          false,
        );
    }

    if (chatId !== "") {
      get().getChatMessages(chatId, null);

      const newChat = chats.find((c) => c.conversation_id === chatId);
      if (newChat) get().setActiveChat(chatId, newChat.receiver_id, true);
    }

    set((state) => {
      return {
        openChatId: chatId,
      };
    });
  },
  connect: (idToken, refreshFn) => {
    if (!idToken) return;
    if (refreshFn) {
      set({ idToken, tokenRefreshFn: refreshFn });
    } else {
      set({ idToken });
    }
    if (get().socket) return;

    const ws = new WebSocket(
      CHATWEBSOCKETENDPOINT + `authorization=${idToken}`,
    );

    ws.onopen = () => {
      set({ isConnected: true });
      get().getChats(null, "en");
      if (get().heartbeatTimer) clearInterval(get().heartbeatTimer!);
      const timer = setInterval(() => {
        const socket = get().socket;
        if (socket && socket.readyState === WebSocket.OPEN) {
          // Send a dummy action to keep connection alive
          socket.send(JSON.stringify({ action: "ping" }));
        }
      }, 540000);

      set({ heartbeatTimer: timer });
    };

    ws.onmessage = (event) => {
      try {
        const data: SocketMessage = JSON.parse(event.data);
        get().dispatchMessage(data);
      } catch (err) {
        // toast("Unable to get conversations.", { type: "error" });
      }
    };

    ws.onclose = async () => {
      if (get().heartbeatTimer) {
        clearInterval(get().heartbeatTimer!);
        set({ heartbeatTimer: null });
      }

      set({ isConnected: false, socket: null });
      if (!get().idToken) return;

      const refreshFn = get().tokenRefreshFn;
      const currentToken = get().idToken;

      // SCENARIO 1: We have a refresh function (The "Smart" Reconnect)
      if (refreshFn) {
        console.log("Attempting to refresh token before reconnecting...");
        try {
          const newToken = await refreshFn();

          if (newToken) {
            console.log("Token refreshed. Reconnecting in 3s...");
            setTimeout(() => {
              get().connect(newToken, refreshFn); // Pass refreshFn again to keep it alive
            }, 3000);
          } else {
            console.error(
              "Token refresh failed. Stopping reconnection attempts.",
            );
            // We do NOTHING here. The socket stays closed.
            // User must refresh the page manually.
            toast("Connection lost. Please refresh the page.", {
              type: "error",
            });
          }
        } catch (e) {
          console.error("Critical error during refresh:", e);
        }
        return; // Exit function
      }

      // SCENARIO 2: No refresh function (Legacy/Fallback behavior)
      // Only try ONE reconnect if we still have a token, then give up?
      // Or just don't reconnect at all if you want to be strict.
      if (currentToken) {
        console.log("Reconnecting with existing token...");
        setTimeout(() => {
          get().connect(currentToken);
        }, 3000);
      }
    };

    ws.onerror = (err) => {};

    set({ socket: ws });
  },
  disconnect: () => {
    set({ idToken: null, tokenRefreshFn: null });

    if (get().heartbeatTimer) {
      clearInterval(get().heartbeatTimer!);
      set({ heartbeatTimer: null });
    }
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
      set({
        socket: null,
        isConnected: false,
        isLoading: true,
        chatsState: EMPTY_STATE,
      });
    }
  },
  dispatchMessage: (data: SocketMessage) => {
    const isSearchMode = get().isSearchMode;
    const openChatId = get().openChatId;

    if (isSearchMode) {
      switch (data.action) {
        case "getChatMessages":
          if (data.data.conversation_id !== openChatId) return;
          break;

        case "sendMessage":
        case "typing":
        case "markAsRead":
          // allow only if it's the opened chat
          if (data.conversation_id !== openChatId) return;
          break;

        default:
          return;
      }
    }
    switch (data.action) {
      case "createChat":
        set((state) => {
          const chat = data.chat;
          const existingChats = [...state.chatsState.chats];
          const chatIndex = existingChats.findIndex(
            (c) => c.conversation_id === chat.conversation_id,
          );

          if (chatIndex !== -1) {
            // Chat already exists, just update participant info
            existingChats[chatIndex] = {
              ...existingChats[chatIndex],
              receiver_complete_name: chat.receiver_complete_name,
              receiver_first_name: chat.receiver_first_name,
              receiver_photo_url: chat.receiver_photo_url,
            };
          } else {
            // New chat → prepend to the list
            const newChat: StoredChat = {
              ...chat,
              messages: [],
              last_cursor: null,
              has_more_pages: true,
            };
            existingChats.unshift(newChat);
          }

          return {
            chatsState: {
              ...state.chatsState,
              chats: existingChats,
            },
          };
        });
        break;
      case "getChatMessages":
        set((state) => {
          const { conversation_id, messages, last_cursor, has_more_pages } =
            data.data;
          const existingChats = [...state.chatsState.chats];
          const chatIndex = existingChats.findIndex(
            (c) => c.conversation_id === conversation_id,
          );
          if (chatIndex !== -1) {
            const existingChat = existingChats[chatIndex];
            // avoid duplicates
            const existingIds = new Set(
              existingChat.messages.map((m) => m.message_id),
            );
            const newMessages = messages.filter(
              (m) => !existingIds.has(m.message_id),
            );

            existingChats[chatIndex] = {
              ...existingChat,
              messages: [...newMessages, ...existingChat.messages],
              last_cursor,
              has_more_pages,
              is_loading_messages: false,
            };
          }
          return {
            chatsState: {
              ...state.chatsState,
              chats: existingChats,
            },
          };
        });
        break;
      case "getChats":
        set((state) => {
          const incomingChats = data.data.chats;
          const currentChats = [...state.chatsState.chats];

          const chatMap = new Map(
            currentChats.map((c) => [c.conversation_id, c]),
          );

          incomingChats.forEach((incoming) => {
            const existing = chatMap.get(incoming.conversation_id);

            if (existing) {
              chatMap.set(incoming.conversation_id, {
                ...incoming,
                messages: existing.messages,
                last_cursor: existing.last_cursor,
                has_more_pages: existing.has_more_pages,
              });
            } else {
              chatMap.set(incoming.conversation_id, {
                ...incoming,
                messages: [],
                last_cursor: null,
                has_more_pages: true,
              });
            }
          });

          const mergedChats = Array.from(chatMap.values());

          mergedChats.sort(
            (a, b) => (b.last_updated_at ?? 0) - (a.last_updated_at ?? 0),
          );

          return {
            chatsState: {
              chats: mergedChats,
              last_cursor: data.data.last_cursor,
              has_more_pages: data.data.has_more_pages,
              total_unread_count: data.data.total_unread_count,
            },
            isLoading: false,
          };
        });
        break;
      case "getChatById":
        console.log(data);
        const chat = data.data.chat;
        if (!chat || !chat.conversation_id) break;
        console.log(chat);
        set((state) => {
          const existingChats = [...state.chatsState.chats];

          // prevent duplicates
          const exists = existingChats.find(
            (c) => c.conversation_id === chat.conversation_id,
          );

          if (exists) {
            return {
              openChatId: chat.conversation_id,
            };
          }

          const newChat: StoredChat = {
            ...chat,
            messages: [],
            last_cursor: null,
            has_more_pages: true,
          };

          // 🔥 insert but keep sorting
          const updatedChats = [...existingChats, newChat].sort(
            (a, b) => (b.last_updated_at ?? 0) - (a.last_updated_at ?? 0),
          );

          return {
            chatsState: {
              ...state.chatsState,
              chats: updatedChats,
            },
            openChatId: chat.conversation_id,
          };
        });

        // fetch messages after inserting
        get().getChatMessages(chat.conversation_id, null);
        break;
      case "chatPresence":
        set((state) => {
          const chats = [...state.chatsState.chats];
          const idx = chats.findIndex(
            (c) => c.conversation_id === data.conversation_id,
          );

          if (idx === -1) return state;

          chats[idx] = {
            ...chats[idx],
            is_receiver_active: data.is_active,
          };

          return {
            chatsState: {
              ...state.chatsState,
              chats,
            },
          };
        });
        break;
      case "markAsRead":
        set((state) => {
          const { conversation_id } = data;
          const existingChats = [...state.chatsState.chats];
          let unreadSubtracted = 0;
          const chatIndex = existingChats.findIndex(
            (c) => c.conversation_id === conversation_id,
          );
          if (chatIndex !== -1) {
            unreadSubtracted = existingChats[chatIndex].unread_count;
            existingChats[chatIndex] = {
              ...existingChats[chatIndex],
              is_read: true,
              unread_count: 0,
            };
          }
          return {
            chatsState: {
              ...state.chatsState,
              chats: existingChats,
              total_unread_count: Math.max(
                0,
                state.chatsState.total_unread_count - unreadSubtracted,
              ),
            },
          };
        });
        break;
      case "sendMessage":
        set((state) => {
          const chats = state.chatsState.chats;

          const chatIndex = chats.findIndex(
            (c) => c.conversation_id === data.conversation_id,
          );
          if (chatIndex === -1) return state;

          const oldChat = chats[chatIndex];

          // Normalize backend payload (senderId → sender_id, receiverId → receiver_id)
          const incomingMessage: Message = {
            ...data.message,
            sender_id:
              (data.message as any).sender_id ?? (data.message as any).senderId,
            receiver_id:
              (data.message as any).receiver_id ??
              (data.message as any).receiverId,
          };

          // Avoid duplicates
          const hasMessage = oldChat.messages.some(
            (m) => m.message_id === incomingMessage.message_id,
          );

          const newMessages = hasMessage
            ? [...oldChat.messages]
            : [...oldChat.messages, incomingMessage];
          let previewText = "";
          if (incomingMessage.type === "assistant") {
            const p = incomingMessage.payload as any; // Cast to access data/ui_type
            const txt = p.data?.text || "";
            const uiType = p.ui_type;

            // 1. Raw Text Types (GPT response)
            if (uiType === "text" || uiType === "listing") {
              previewText = txt || "Sent a message";
            }
            // 2. System/Key Types -> Construct the key: "assistant.{ui_type}.{text}"
            else if (uiType && txt) {
              previewText = `assistant.${uiType}.${txt}`;
            }
            // 3. Fallback
            else {
              previewText = "assistant.info.no_response";
            }
          } else {
            // Handle other message types (Text, Media, Listing)
            // This matches the logic you had, or you can align it with backend "shared-property" etc.
            previewText =
              (incomingMessage.payload as any).text ?? "new-message";
          }

          const newChat = {
            ...oldChat,

            messages: newMessages,
            last_message: {
              type: incomingMessage.type,
              preview: previewText,
              timestamp: incomingMessage.timestamp,
              sender_id: incomingMessage.sender_id,
            },
            last_updated_at: incomingMessage.timestamp,
            is_assistant_typing: incomingMessage.sender_id.startsWith(
              "assistant",
            )
              ? false
              : oldChat.is_assistant_typing,
          };

          // Move chat to top
          const newChats = [
            newChat,
            ...chats.filter((_, i) => i !== chatIndex),
          ];
          const isIncoming =
            incomingMessage.sender_id !== useChatStore.getState().idToken;
          const isUnread =
            isIncoming && data.conversation_id !== get().openChatId;

          return {
            chatsState: {
              ...state.chatsState,
              chats: newChats,
              total_unread_count: isUnread
                ? state.chatsState.total_unread_count + 1
                : state.chatsState.total_unread_count,
            },
          };
        });
        break;
      case "typing":
        set((state) => {
          const chats = [...state.chatsState.chats];
          const idx = chats.findIndex(
            (c) => c.conversation_id === data.conversation_id,
          );

          if (idx === -1) return state;

          chats[idx] = {
            ...chats[idx],
            is_assistant_typing: data.is_typing,
          };

          return {
            chatsState: {
              ...state.chatsState,
              chats,
            },
          };
        });
        break;
    }
  },
  createChat: (messageType, payload, secondUserSub, isAiChat = false) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const newChatId = uuidv4();

      ws.send(
        JSON.stringify({
          action: "createChat",
          conversation_id: newChatId,
          message_type: messageType,
          payload: payload,
          second_user_sub: secondUserSub,
          is_ai_chat: isAiChat,
        }),
      );

      return newChatId;
    } else {
      toast("Unable to start conversation with user.", { type: "error" });
      return null;
    }
  },
  deleteMessage: (conversationId, messageId, secondUserSub) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "deleteMessage",
          conversation_id: conversationId,
          message_id: messageId,
          second_user_sub: secondUserSub,
        }),
      );
    } else {
      toast("Unable to delete message.", { type: "error" });
    }
  },
  getChatMessages: (conversationId, cursor) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (!cursor) {
        set((state) => {
          const chats = [...state.chatsState.chats];
          const idx = chats.findIndex(
            (c) => c.conversation_id === conversationId,
          );
          if (idx !== -1) {
            chats[idx] = { ...chats[idx], is_loading_messages: true };
          }
          return { chatsState: { ...state.chatsState, chats } };
        });
      }

      ws.send(
        JSON.stringify({
          action: "getChatMessages",
          conversation_id: conversationId,
          cursor: cursor ? JSON.stringify(cursor) : "",
        }),
      );
    } else {
      toast("Unable to get conversation messages.", { type: "error" });
    }
  },
  getChats: (cursor, locale) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "getChats",
          cursor: cursor ? JSON.stringify(cursor) : "",
          locale: locale,
        }),
      );
    } else {
      toast("Unable to get conversations.", { type: "error" });
    }
  },
  getChatById: (conversationId) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "getChatById",
          chat_id: conversationId,
        }),
      );
    } else {
      toast("Unable to get conversation.", { type: "error" });
    }
  },
  markAsRead: (conversationId) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "markAsRead",
          conversation_id: conversationId,
        }),
      );
    } else {
      toast("Unable to mark conversation as read.", { type: "error" });
    }
  },
  sendMessage: (conversationId, messageType, payload, secondUserSub) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "sendMessage",
          conversation_id: conversationId,
          message_type: messageType,
          payload: payload,
          second_user_sub: secondUserSub,
        }),
      );
    } else {
      toast("Unable to send message.", { type: "error" });
    }
  },
  setActiveChat: (conversationId, secondUserSub, isActive) => {
    const ws = get().socket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          action: "setActiveChat",
          conversation_id: conversationId,
          second_user_sub: secondUserSub,
          is_active: isActive,
        }),
      );
    }
  },
  updateMessages: (conversationId, fn) => {
    set((state) => {
      const chats = state.chatsState.chats;

      const idx = chats.findIndex((c) => c.conversation_id === conversationId);

      if (idx === -1) return state;

      const chat = chats[idx];

      const updatedMessages = fn(chat.messages);

      // prevent unnecessary updates
      if (updatedMessages === chat.messages) return state;

      const updatedChat = {
        ...chat,
        messages: updatedMessages,
      };

      const newChats = [...chats];
      newChats[idx] = updatedChat;

      return {
        chatsState: {
          ...state.chatsState,
          chats: newChats,
        },
      };
    });
  },
}));
